package dev.danko.roblox_craft_ai.uikit.components

import com.varabyte.kobweb.compose.foundation.layout.Spacer
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.Resize
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.compose.runtime.rememberCoroutineScope
import dev.danko.roblox_craft_ai.shared.model.MeshViews
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.events.SyntheticInputEvent
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import kotlin.random.Random

/** The composer card (style A): one surface holding type, text, attachments and actions. */
val ComposerStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(14.px)
        .borderRadius(18.px)
        .border(1.px, LineStyle.Solid, palette.borderStrong)
        .backgroundColor(palette.surface)
}

val SegmentedStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .padding(4.px)
        .borderRadius(12.px)
        .backgroundColor(palette.background)
        .gap(4.px)
        .flexWrap(FlexWrap.Wrap)
        .alignSelf(AlignSelf.FlexStart)
        .maxWidth(100.percent)
}

val SegmentStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .height(36.px)
            .padding(leftRight = 12.px)
            .borderRadius(8.px)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .backgroundColor(Colors.Transparent)
            .color(palette.textSecondary)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(6.px)
            .whiteSpace(WhiteSpace.NoWrap)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier.color(palette.textPrimary)
    }
}

val SegmentActiveStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier.backgroundColor(palette.border).color(palette.textPrimary)
}

val ComposerTextAreaStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .minHeight(112.px)
        .padding(topBottom = 6.px, leftRight = 8.px)
        .border(0.px, LineStyle.None, Colors.Transparent)
        .outline(0.px, LineStyle.None, Colors.Transparent)
        .backgroundColor(Colors.Transparent)
        .color(palette.textPrimary)
        .fontSize(17.px)
        .lineHeight(1.55)
        .resize(Resize.Vertical)
        .styleModifier { property("font-family", "inherit") }
}

val OptionsPanelStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(12.px)
        .borderRadius(12.px)
        .backgroundColor(palette.background)
        .border(1.px, LineStyle.Solid, palette.border)
}

val RefThumbnailContainerStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .width(56.px)
        .height(56.px)
        .borderRadius(10.px)
        .border(1.px, LineStyle.Solid, palette.borderStrong)
        .backgroundColor(palette.background)
        .position(Position.Relative)
        .styleModifier { property("overflow", "visible") }
}

@Composable
fun PromptBox(
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    initialType: AssetType = AssetType.LUAU_SCRIPT,
    initialProviderId: String? = null,
    onCancel: (() -> Unit)? = null,
    prefillPrompt: String? = null,
    prefillType: AssetType? = null,
    prefillProviderId: String? = null,
    flashTrigger: Boolean = false,
    showModelPicker: Boolean = true,
    /** Extra rarely-used options shown under "Options" (save as idea, queue, sketch first…). */
    extraOptions: (@Composable () -> Unit)? = null,
    /** Credit cost label for the selected type, shown next to the Create button. */
    costFor: ((AssetType) -> String?)? = null,
    /** Always-visible choices for the selected type (e.g. quality for 3D models), above the Create button. */
    typeOptions: (@Composable (AssetType) -> Unit)? = null,
    /** Cuts a model sheet into named views (MeshViews); without it pictures fill the 3D view slots one by one. */
    splitSheet: (suspend (String) -> Map<String, String>)? = null,
    /** referenceViews names each reference image (MeshViews) for 3D models; empty for other types. */
    onSubmit: (prompt: String, type: AssetType, providerId: String?, referenceImages: List<String>, referenceViews: List<String>) -> Unit
) {
    val theme = LocalAppTheme.current
    val palette = theme.colors
    val s = strings()
    var prompt by remember { mutableStateOf("") }
    var selectedType by remember(initialType) { mutableStateOf(initialType) }
    var selectedProviderId by remember(initialProviderId) { mutableStateOf(initialProviderId) }
    var attachedImages by remember { mutableStateOf<List<String>>(emptyList()) }
    // 3D models take named views instead: front, back, left, right.
    var views by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var pickingView by remember { mutableStateOf<String?>(null) }
    var splitting by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val is3d = selectedType == AssetType.MESH_3D
    var isFocused by remember { mutableStateOf(false) }
    var isDragOver by remember { mutableStateOf(false) }
    var dragDepth by remember { mutableStateOf(0) }
    var toastText by remember { mutableStateOf<String?>(null) }
    var isFlashing by remember { mutableStateOf(false) }
    var optionsOpen by remember { mutableStateOf(false) }
    val fileInputId = remember { "ref-image-input-${Random.nextInt(1000000)}" }
    val taId = remember { "prompt-textarea-${Random.nextInt(1000000)}" }
    val anchorId = "prompt-box-anchor"

    LaunchedEffect(Unit) {
        delay(100)
        val el = document.getElementById(taId) as? HTMLTextAreaElement
        if (el != null && el.value.isNotBlank() && prompt.isBlank()) {
            prompt = el.value
        }
    }

    LaunchedEffect(prefillPrompt, prefillType, prefillProviderId) {
        prefillPrompt?.let { if (it.isNotBlank()) prompt = it }
        prefillType?.let { selectedType = it }
        prefillProviderId?.let { selectedProviderId = it }
    }

    LaunchedEffect(flashTrigger) {
        if (flashTrigger) {
            isFlashing = true
            delay(800)
            isFlashing = false
        }
    }

    LaunchedEffect(toastText) {
        if (toastText != null) {
            delay(2500)
            toastText = null
        }
    }

    val providers = listOf(
        null to s.promptBox.autoProvider,
        "glm-5.3-flash" to "💚 GLM 5.3 Flash",
        "gemini-3.7-flash" to "💛 Gemini 3.7 Flash"
    )

    val generateText = s.assetTypes.generateButton(selectedType)

    fun showToast(msg: String) {
        toastText = msg
    }

    /** A picture for the 3D view slots: into the chosen slot, a sheet cut into all of them, or the first free one. */
    fun addView(dataUrl: String) {
        pickingView?.let { view ->
            views = views + (view to dataUrl)
            pickingView = null
            return
        }
        fun intoFreeSlot() {
            MeshViews.ALL.firstOrNull { it !in views }?.let { views = views + (it to dataUrl) } ?: showToast(s.promptBox.viewsTooMany)
        }
        val split = splitSheet
        if (split == null || views.isNotEmpty()) return intoFreeSlot()
        splitting = true
        scope.launch {
            val found = try { split(dataUrl) } catch (_: Throwable) { emptyMap() }
            splitting = false
            if (found.size > 1) {
                views = found.filterKeys { it in MeshViews.ALL }
                showToast(s.promptBox.viewsSplit(views.size))
            } else {
                intoFreeSlot()
            }
        }
    }

    fun processIncomingFiles(fileList: dynamic) {
        val maxFiles = if (is3d) MeshViews.ALL.size else 3
        val maxSizeBytes = 5L * 1024 * 1024
        val currentSize = if (is3d) (if (pickingView != null) maxFiles - 1 else views.size) else attachedImages.size
        val remainingSlots = maxFiles - currentSize

        if (remainingSlots <= 0) {
            showToast(if (is3d) s.promptBox.viewsTooMany else s.promptBox.tooManyReferences)
            return
        }

        val filesToProcess = mutableListOf<File>()
        var hasNonImage = false
        var hasOversized = false
        var totalImages = 0

        val len: Int = (fileList.length as? Int) ?: 0
        for (i in 0 until len) {
            val file = fileList.item(i) as? File ?: continue
            if (!file.type.startsWith("image/")) {
                hasNonImage = true
                continue
            }
            totalImages++
            val fileSize = (file.size as? Number)?.toLong() ?: 0L
            if (fileSize > maxSizeBytes) {
                hasOversized = true
                continue
            }
            if (filesToProcess.size < remainingSlots) {
                filesToProcess.add(file)
            }
        }

        if (totalImages > remainingSlots) {
            showToast(s.promptBox.tooManyReferences)
        } else if (hasNonImage && filesToProcess.isEmpty()) {
            showToast(s.promptBox.onlyImages)
        } else if (hasOversized && filesToProcess.isEmpty()) {
            showToast(s.promptBox.fileTooLarge)
        }

        if (filesToProcess.isEmpty() && hasNonImage) {
            showToast(s.promptBox.onlyImages)
            return
        }

        filesToProcess.forEach { file ->
            val reader = js("new FileReader()")
            reader.onload = { loadEv: dynamic ->
                val dataUrl = loadEv.target.result as String
                if (dataUrl.isNotBlank() && is3d) {
                    addView(dataUrl)
                } else if (dataUrl.isNotBlank() && attachedImages.size < maxFiles) {
                    attachedImages = attachedImages + dataUrl
                }
            }
            reader.readAsDataURL(file)
        }
    }

    fun submitCurrentPrompt() {
        console.log("[PromptBox] submitCurrentPrompt called. taId=$taId, prompt='$prompt', isLoading=$isLoading")
        val el = (document.getElementById(taId) as? HTMLTextAreaElement)
            ?: (document.querySelector("textarea") as? HTMLTextAreaElement)
        console.log("[PromptBox] textarea element found: ${el != null}, value='${el?.value}'")
        val textFromDom = el?.value?.trim() ?: ""
        val effective = when {
            textFromDom.isNotBlank() -> textFromDom
            prompt.isNotBlank() -> prompt.trim()
            (if (is3d) views.isNotEmpty() else attachedImages.isNotEmpty()) -> s.promptBox.promptFromImages(selectedType)
            else -> ""
        }
        if (effective.isBlank()) {
            showToast(s.promptBox.emptyPrompt)
            return
        }
        console.log("[PromptBox] effective='$effective', selectedType=$selectedType, providerId=$selectedProviderId, images=${attachedImages.size}")
        prompt = effective
        console.log("[PromptBox] calling onSubmit...")
        try {
            val viewNames = if (is3d) MeshViews.ALL.filter { it in views } else emptyList()
            val finalImages = if (is3d) viewNames.map { views.getValue(it) } else attachedImages.take(3)
            onSubmit(effective, selectedType, if (showModelPicker) selectedProviderId else null, finalImages, viewNames)
            console.log("[PromptBox] onSubmit returned successfully")
        } catch (t: Throwable) {
            console.log("[PromptBox] onSubmit THREW: ${t.message}")
            console.log(t.asDynamic().stack)
        }
    }

    val outerCardModifier = Modifier
        .fillMaxWidth()
        .id(anchorId)
        .then(if (isFlashing) Modifier.border(3.px, LineStyle.Solid, palette.accent) else Modifier)
        .then(modifier)

    Box(
        ComposerStyle.toModifier()
            .then(outerCardModifier)
            .then(if (isFocused) Modifier.border(1.px, LineStyle.Solid, palette.accent) else Modifier)
            .position(Position.Relative)
    ) {
        Column(Modifier.fillMaxWidth().gap(12.px)) {
            // Asset type: a segmented control above the text, as in the approved mockup.
            Row(
                SegmentedStyle.toModifier().attrsModifier { attr("role", "tablist"); attr("aria-label", s.promptBox.assetTypeLabel) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                AssetType.entries.forEach { type ->
                    val selected = selectedType == type
                    org.jetbrains.compose.web.dom.Button(
                        SegmentStyle.toModifier()
                            .then(if (selected) SegmentActiveStyle.toModifier() else Modifier)
                            .onClick { selectedType = type }
                            .toAttrs {
                                attr("type", "button")
                                attr("role", "tab")
                                attr("aria-selected", selected.toString())
                            }
                    ) {
                        Icon(
                            when (type) {
                                AssetType.LUAU_SCRIPT -> AppIcon.CODE
                                AssetType.MESH_3D -> AppIcon.CUBE
                                AssetType.ANIMATION -> AppIcon.FIGURE
                                AssetType.ROJO_PROJECT -> AppIcon.FOLDER
                            },
                            size = 16
                        )
                        SpanText(s.assetTypes.name(type))
                        if (s.assetTypes.availabilityNote(type) != null) {
                            SpanText(s.common.betaBadge, Modifier.fontSize(11.px).color(palette.statusPending).fontWeight(FontWeight.Bold))
                        }
                    }
                }
            }

            TextArea(
                attrs = ComposerTextAreaStyle.toModifier().id(taId).toAttrs {
                    placeholder(s.promptBox.placeholder)
                    attr("aria-label", s.promptBox.label)
                    attr("rows", "4")
                    // Always controlled: skipping value("") let the DOM restore the last character after deleting it.
                    value(prompt)
                    onInput { event: SyntheticInputEvent<String, HTMLTextAreaElement> ->
                        prompt = event.value
                    }
                    onFocusIn {
                        isFocused = true
                        val el2 = document.getElementById(taId) as? HTMLTextAreaElement
                        if (el2 != null && el2.value.isNotBlank()) prompt = el2.value
                    }
                    onFocusOut {
                        isFocused = false
                        val el2 = document.getElementById(taId) as? HTMLTextAreaElement
                        if (el2 != null && el2.value.isNotBlank()) prompt = el2.value
                    }
                    onKeyDown { event ->
                        if ((event.ctrlKey || event.metaKey) && event.key == "Enter") {
                            submitCurrentPrompt()
                        }
                    }
                    onPaste { event ->
                        // clipboardData lives on the browser event, not on Compose's synthetic wrapper.
                        val files = event.nativeEvent.asDynamic().clipboardData?.files
                        if (files != null && js("Array.from(files).some(function(f) { return f.type.indexOf('image/') === 0 })") as Boolean) {
                            event.preventDefault()
                            processIncomingFiles(files)
                        }
                    }
                }
            )

            if (is3d) {
                ViewSlots(
                    views = views,
                    splitting = splitting,
                    onPick = { view ->
                        pickingView = view
                        (document.getElementById(fileInputId) as? HTMLInputElement)?.click()
                    },
                    onRemove = { view -> views = views - view },
                    onSwapSides = {
                        views = views - MeshViews.LEFT - MeshViews.RIGHT +
                            listOfNotNull(views[MeshViews.RIGHT]?.let { MeshViews.LEFT to it }, views[MeshViews.LEFT]?.let { MeshViews.RIGHT to it })
                    }
                )
            }

            // Attached pictures appear only when there are some — no empty slots.
            if (!is3d && attachedImages.isNotEmpty()) {
                Row(Modifier.gap(8.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(s.promptBox.referencesLabel.removePrefix("🖼️ "), Modifier.fontSize(13.px).color(palette.textSecondary))
                    attachedImages.forEachIndexed { slotIdx, dataUrl ->
                        Box(RefThumbnailContainerStyle.toModifier()) {
                            Img(src = dataUrl, attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(ObjectFit.Cover).borderRadius(10.px).toAttrs())
                            org.jetbrains.compose.web.dom.Button(
                                Modifier
                                    .position(Position.Absolute)
                                    .top((-6).px)
                                    .right((-6).px)
                                    .size(22.px)
                                    .padding(0.px)
                                    .borderRadius(50.percent)
                                    .border(1.px, LineStyle.Solid, palette.borderStrong)
                                    .backgroundColor(palette.surfaceElevated)
                                    .color(palette.textPrimary)
                                    .cursor(Cursor.Pointer)
                                    .display(DisplayStyle.Flex)
                                    .alignItems(AlignItems.Center)
                                    .justifyContent(JustifyContent.Center)
                                    .onClick { attachedImages = attachedImages.filterIndexed { i, _ -> i != slotIdx } }
                                    .toAttrs { attr("type", "button"); attr("aria-label", s.common.delete) }
                            ) { Icon(AppIcon.CLOSE, size = 12) }
                        }
                    }
                    SpanText("${attachedImages.size}/3", Modifier.fontSize(13.px).color(palette.textSecondary))
                }
            }

            s.assetTypes.availabilityNote(selectedType)?.let { note ->
                SpanText(note, Modifier.fontSize(13.px).color(palette.textSecondary).padding(leftRight = 8.px))
            }

            typeOptions?.let { options ->
                Column(Modifier.fillMaxWidth().padding(leftRight = 8.px)) { options(selectedType) }
            }

            if (optionsOpen) {
                Column(OptionsPanelStyle.toModifier().gap(12.px)) {
                    if (showModelPicker) {
                        SpanText(s.promptBox.aiModelLabel, Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
                        Row(Modifier.gap(6.px).flexWrap(FlexWrap.Wrap)) {
                            providers.forEach { (id, label) ->
                                AppButton(
                                    text = label,
                                    onClick = { selectedProviderId = id },
                                    variant = if (selectedProviderId == id) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE,
                                    size = AppButtonSize.SM
                                )
                            }
                        }
                    }
                    extraOptions?.invoke()
                }
            }

            Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                AppButton(
                    text = "",
                    icon = AppIcon.PAPERCLIP,
                    ariaLabel = s.promptBox.attachReference,
                    variant = AppButtonVariant.SECONDARY,
                    size = AppButtonSize.MD,
                    modifier = Modifier.width(44.px).padding(0.px).title(s.promptBox.attachReference),
                    onClick = { (document.getElementById(fileInputId) as? HTMLInputElement)?.click() }
                )
                Input(
                    type = InputType.File,
                    attrs = Modifier.display(DisplayStyle.None).id(fileInputId).toAttrs {
                        attr("accept", "image/*")
                        attr("multiple", "true")
                        onInput { event ->
                            val target = event.target as? HTMLInputElement
                            target?.files?.let { processIncomingFiles(it) }
                            target?.value = ""
                        }
                    }
                )
                if (showModelPicker || extraOptions != null) {
                    AppButton(
                        text = s.home.options,
                        icon = AppIcon.SLIDERS,
                        variant = if (optionsOpen) AppButtonVariant.PRIMARY else AppButtonVariant.SECONDARY,
                        size = AppButtonSize.MD,
                        onClick = { optionsOpen = !optionsOpen }
                    )
                }
                Spacer()
                costFor?.invoke(selectedType)?.let { cost ->
                    SpanText(cost, Modifier.fontSize(13.px).color(palette.textSecondary))
                }
                if (isLoading) {
                    AppButton(
                        text = s.promptBox.generating,
                        variant = AppButtonVariant.PRIMARY,
                        size = AppButtonSize.MD,
                        loading = true,
                        enabled = false,
                        onClick = {}
                    )
                    if (onCancel != null) {
                        AppButton(text = s.common.cancel, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.MD, onClick = onCancel)
                    }
                } else {
                    AppButton(
                        text = generateText,
                        icon = AppIcon.SPARKLES,
                        variant = AppButtonVariant.PRIMARY,
                        size = AppButtonSize.MD,
                        onClick = { submitCurrentPrompt() }
                    )
                }
            }
        }

        if (isDragOver) {
            val overlayMod = Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
                .zIndex(999)
                .backgroundColor(palette.accent.toRgb().copy(alpha = 40))
                .border(2.px, LineStyle.Dashed, palette.accent)
                .borderRadius(18.px)
                .onDragEnter {
                    it.preventDefault()
                    dragDepth++
                }
                .onDragOver { ev -> ev.preventDefault() }
                .onDragLeave {
                    dragDepth = (dragDepth - 1).coerceAtLeast(0)
                    if (dragDepth == 0) isDragOver = false
                }
                .onDrop { ev ->
                    ev.preventDefault()
                    dragDepth = 0
                    isDragOver = false
                    processIncomingFiles(ev.nativeEvent.asDynamic().dataTransfer.files)
                }
            Div(attrs = overlayMod.toAttrs {
                attr("role", "region")
                attr("aria-live", "polite")
                attr("aria-label", s.promptBox.dropAria)
            }) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center,
                    modifier = Modifier.gap(8.px).fillMaxWidth().fillMaxHeight().color(palette.textPrimary)
                ) {
                    Icon(AppIcon.PAPERCLIP, size = 32)
                    SpanText(s.promptBox.dropTitle.removePrefix("+ "), Modifier.fontSize(17.px).fontWeight(FontWeight.Bold))
                    SpanText(s.promptBox.dropHint, Modifier.fontSize(14.px).color(palette.textSecondary))
                }
            }
        }
    }

    DisposableEffect(Unit) {
        val cardEl = document.getElementById(anchorId)
        val onDragEnterExternal: (Event) -> Unit = { ev ->
            ev.preventDefault()
            val dt = ev.asDynamic().dataTransfer
            if (dt != null && dt.types != undefined) {
                val hasFiles = js("Array.from(dt.types).includes('Files')") as Boolean
                if (hasFiles) {
                    dragDepth++
                    isDragOver = true
                }
            }
        }
        val onDragOverExternal: (Event) -> Unit = { ev ->
            ev.preventDefault()
        }
        val onDropExternal: (Event) -> Unit = { ev ->
            ev.preventDefault()
            dragDepth = 0
            isDragOver = false
            val dt = ev.asDynamic().dataTransfer
            val files = dt?.files
            if (files != null) {
                processIncomingFiles(files)
            }
        }
        if (cardEl != null) {
            cardEl.addEventListener("dragenter", onDragEnterExternal)
            cardEl.addEventListener("dragover", onDragOverExternal)
            cardEl.addEventListener("drop", onDropExternal)
        }

        onDispose {
            if (cardEl != null) {
                cardEl.removeEventListener("dragenter", onDragEnterExternal)
                cardEl.removeEventListener("dragover", onDragOverExternal)
                cardEl.removeEventListener("drop", onDropExternal)
            }
        }
    }

    val toastMsg = toastText
    if (toastMsg != null) {
        val isError = toastMsg.startsWith("⚠")
        val toastBg = if (isError) palette.statusFailed else palette.statusReady
        Box(
            Modifier
                .position(Position.Fixed)
                .bottom(2.cssRem)
                .right(2.cssRem)
                .backgroundColor(toastBg)
                .color(Colors.White)
                .padding(topBottom = 0.75.cssRem, leftRight = 1.cssRem)
                .borderRadius(0.5.cssRem)
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .zIndex(9999)
                .boxShadow(0.px, 8.px, 24.px, 0.px, Color.rgba(0, 0, 0, 50))
                .maxWidth(90.percent),
            contentAlignment = Alignment.Center
        ) {
            SpanText(toastMsg)
        }
    }
}
